<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import { watch } from 'vue'
import * as yup from 'yup'
import { type RolePayload, updateRoleAction } from '@/api/functions/access/roles-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  modelValue: Boolean,
  selectedRole: { type: Object, default: null },
  permissionsOptions: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()
const queryClient = useQueryClient()

const schema = yup.object({
  name: yup.string().required('نام نقش الزامی است').min(3).max(100),
  permissions: yup.array().of(yup.number()).min(1, 'انتخاب حداقل یک مجوز الزامی است'),
})

const { handleSubmit, errors, setValues, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    name: '',
    permissions: [] as number[],
  },
})

const { value: name } = useField('name')
const { value: permissions } = useField('permissions')

watch(
  () => props.selectedRole,
  (role) => {
    if (!role) {
      resetForm()
      return
    }
    setValues({
      name: role.name || '',
      permissions: role.permissions?.map((p: { id: string | number }) => Number(p.id)) || [],
    })
  },
  { immediate: true }
)

const updateRoleMutation = useMutationApi({
  mutationFn: (payload: RolePayload) => updateRoleAction(props.selectedRole.id, { ...payload }),
  onSuccess: () => {
    toast.success('نقش با موفقیت ویرایش شد')
    emit('confirm')
    closeModal()
    queryClient.invalidateQueries({ queryKey: ['roles'] })
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const onSubmit = handleSubmit((values) => {
  if (!props.selectedRole?.id) return

  const payload: RolePayload = {
    name: values.name,
    permissions: values.permissions,
  }

  updateRoleMutation.mutate(payload)
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
    background="#FFF" :closable="true" title="ویرایش نقش" closeDirection="left" class="update-request">
    <form @submit.prevent="onSubmit" class="flex flex-col min-h-[250px]">
      <div class="flex-1">
        <div class="lg:grid grid-cols-2 px-4 pt-6 pb-9 gap-6">
          <Input v-model="name" name="name" label="نام نقش را وارد کنید" type="text" :error="errors.name" />
          <MultiSelect v-model="permissions" :options="permissionsOptions" label="مجوزها را انتخاب کنید"
            :error="errors.permissions" variant="checkbox" />

        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="ویرایش" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
          :disabled="updateRoleMutation.isPending.value" :loading="updateRoleMutation.isPending.value" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" :disabled="updateRoleMutation.isPending.value" />
      </div>
    </form>
  </Modal>
</template>
